import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from '@material-ui/core'
import AccessTimeIcon from '@material-ui/icons/AccessTime'
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline'

import Button from '@/components/button'
import { useCreateInfestationReport } from '@/hooks/mutations/use-create-infestation-report'
import { InfestationReport } from '@/hooks/queries/use-get-infestation-reports'
import { transformUnderscoreInSpaceAndUppercaseFirstLetter } from '@/utils/format-strings'

import { useStyles } from './styles'

interface ModalProps {
  row: InfestationReport
  open: boolean
  onClose: () => void
}

export function Modal({ row, open, onClose }: ModalProps) {
  const classes = useStyles()

  const { mutate, isLoading } = useCreateInfestationReport()

  const ORDERED_BY_MAP = {
    demand_name: 'Nome da demanda (A-Z)',
    '-demand_name': 'Nome da demanda (Z-A)',
    created_at: 'Data de cadastro (mais recente)',
    '-created_at': 'Data de cadastro (mais antigo)',
    infestation_percentage: 'Porcentagem de infestação (mais alta)',
    '-infestation_percentage': 'Porcentagem de infestação (mais baixa)',
    total_area: 'Área total (crescente)',
    '-total_area': 'Área total (decrescente)',
  }

  const orderedByMap =
    ORDERED_BY_MAP[row.ordered_by as keyof typeof ORDERED_BY_MAP]

  function formattedWeedClasses(weedClasses: string[]) {
    return weedClasses
      .map((weedClasse) =>
        transformUnderscoreInSpaceAndUppercaseFirstLetter(weedClasse),
      )
      .join(', ')
  }

  return (
    <Dialog className={classes.main} open={open} onClose={onClose}>
      <DialogTitle disableTypography={true}>
        {row.status.status_key === 'completed' ? (
          <>
            <CheckCircleOutlineIcon />
            <Typography variant='h3'>Relatório entregue</Typography>
          </>
        ) : (
          <>
            <AccessTimeIcon />
            <Typography variant='h3'>Processando solicitação</Typography>
          </>
        )}
      </DialogTitle>
      <DialogContent>
        <DialogContentText component={'div'}>
          <Typography variant='body1'>
            {row.status.status_key === 'completed'
              ? 'O relatório foi enviado para o e-mail'
              : 'O relatório será enviado para o e-mail'}
            <strong> {row.requester.email}</strong>
          </Typography>
          <div className={classes.info}>
            <div className={classes.infoRow}>
              <Typography variant='subtitle1'>
                Período dos resultados:
              </Typography>
              <Typography variant='subtitle2'>
                {row.initial_conclusion_date} a {row.final_conclusion_date}
              </Typography>
            </div>
            <div className={classes.infoRow}>
              <Typography variant='subtitle1'>Nome do relatório:</Typography>
              <Typography variant='subtitle2'>{row.name}</Typography>
            </div>

            {row.weed_type_by_culture.cane.length > 0 && (
              <div className={classes.infoRow}>
                <Typography variant='subtitle1'>
                  Classes de cana daninhas:
                </Typography>
                <Typography variant='subtitle2'>
                  {row.weed_type_by_culture.cane.length === 6
                    ? 'Todas as classes'
                    : formattedWeedClasses(row.weed_type_by_culture.cane)}
                </Typography>
              </div>
            )}
            {row.weed_type_by_culture.soybean.length > 0 && (
              <div className={classes.infoRow}>
                <Typography variant='subtitle1'>
                  Classes de soja daninhas:
                </Typography>
                <Typography variant='subtitle2'>
                  {row.weed_type_by_culture.soybean.length === 6
                    ? 'Todas as classes'
                    : formattedWeedClasses(row.weed_type_by_culture.soybean)}
                </Typography>
              </div>
            )}
            <div className={classes.infoRow}>
              <Typography variant='subtitle1'>
                Ordenação do relatório:
              </Typography>
              <Typography variant='subtitle2'>{orderedByMap}</Typography>
            </div>
            <div className={classes.infoRow}>
              <Typography variant='subtitle1'>Data da solicitação:</Typography>
              <Typography variant='subtitle2'>{row.created_at}</Typography>
            </div>
            <div className={classes.infoRow}>
              <Typography variant='subtitle1'>Hora da solicitação:</Typography>
              <Typography variant='subtitle2'>
                {row.time_from_created_at}
              </Typography>
            </div>
          </div>
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button
          outlined
          onClick={() =>
            mutate({
              id: row.id,
              initial_conclusion_date: row.initial_conclusion_date,
              final_conclusion_date: row.final_conclusion_date,
              ordered_by: row.ordered_by,
              weed_type_by_culture: row.weed_type_by_culture,
            })
          }
          loading={isLoading}
          disabled={row.status.status_key === 'processing'}
        >
          Reenviar relatório
        </Button>
        <Button onClick={onClose}>Fechar</Button>
      </DialogActions>
    </Dialog>
  )
}
